import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireAdmin } from "@/integrations/supabase/role-middleware";
import { buildExcerpt, ensureUniqueSlug, slugifyTitle } from "@/lib/blog-helpers";
import { optionalHttpUrlOrEmpty, publishedOnCreate, publishedOnUpdate } from "@/lib/content-form";
import { siteNow, toUtcIso } from "@/lib/datetime.schedule";

export enum PracticeTopic {
  Putting = "putting",
  Driving = "driving",
  Chipping = "chipping",
  Iron = "iron",
  Wedge = "wedge",
  FullSwing = "full-swing",
}

export const PracticeTopicLabels = {
  [PracticeTopic.Putting]: "Putting",
  [PracticeTopic.Driving]: "Driving",
  [PracticeTopic.Chipping]: "Chipping",
  [PracticeTopic.Iron]: "Iron",
  [PracticeTopic.Wedge]: "Wedge",
  [PracticeTopic.FullSwing]: "Full Swing",
} as const;

const PRACTICE_TOPICS = [
  PracticeTopic.Putting,
  PracticeTopic.Driving,
  PracticeTopic.Chipping,
  PracticeTopic.Iron,
  PracticeTopic.Wedge,
  PracticeTopic.FullSwing,
] as const;

const entryFields =
  "id, slug, title, excerpt, body_markdown, topic, preview_image_url, source_blog_post_id, published, published_at, created_at, updated_at";

const entryInput = z.object({
  title: z.string().trim().min(1).max(200),
  body_markdown: z.string().min(1),
  excerpt: z.string().max(500).optional().nullable(),
  topic: z.enum(PRACTICE_TOPICS),
  preview_image_url: optionalHttpUrlOrEmpty,
  published: z.boolean().optional(),
  published_at: z.string().datetime().optional(),
});

const promoteFromBlogInput = z.object({
  sourceBlogPostId: z.string().uuid(),
  title: z.string().trim().min(1).max(200),
  body_markdown: z.string().min(1),
  excerpt: z.string().max(500).optional().nullable(),
  topic: z.enum(PRACTICE_TOPICS),
  preview_image_url: optionalHttpUrlOrEmpty,
});

export const listPracticeLibraryItems = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("practice_library_entries")
    .select(entryFields)
    .eq("published", true)
    .order("published_at", { ascending: false });
  if (error) throw new Error(error.message);
  return data ?? [];
});

export const getPracticeLibraryEntryBySlug = createServerFn({ method: "GET" })
  .validator((d) => z.object({ slug: z.string().min(1) }).parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: entry, error } = await supabaseAdmin
      .from("practice_library_entries")
      .select(entryFields)
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!entry) throw new Error("Practice drill not found");
    return entry;
  });

export const listAllPracticeLibraryEntries = createServerFn({ method: "GET" })
  .middleware([requireAdmin])
  .handler(async ({ context }) => {
    const { supabase } = context;
    const { data, error } = await supabase
      .from("practice_library_entries")
      .select(entryFields)
      .order("published_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const createPracticeLibraryEntry = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((d) => entryInput.parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const excerpt = buildExcerpt(data.body_markdown, data.excerpt);
    const slug = await ensureUniqueSlug(
      supabase,
      "practice_library_entries",
      slugifyTitle(data.title),
      undefined,
      "drill",
    );
    const { data: entry, error } = await supabase
      .from("practice_library_entries")
      .insert({
        slug,
        title: data.title,
        body_markdown: data.body_markdown,
        excerpt,
        topic: data.topic,
        preview_image_url: data.preview_image_url,
        ...publishedOnCreate(data),
      })
      .select(entryFields)
      .single();
    if (error) throw new Error(error.message);
    return entry;
  });

export const updatePracticeLibraryEntry = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((d) => entryInput.extend({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const excerpt = buildExcerpt(data.body_markdown, data.excerpt);
    const { data: entry, error } = await supabase
      .from("practice_library_entries")
      .update({
        title: data.title,
        body_markdown: data.body_markdown,
        excerpt,
        topic: data.topic,
        preview_image_url: data.preview_image_url,
        ...publishedOnUpdate(data),
      })
      .eq("id", data.id)
      .select(entryFields)
      .single();
    if (error) throw new Error(error.message);
    return entry;
  });

export const deletePracticeLibraryEntry = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { error } = await supabase.from("practice_library_entries").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Snapshot a blog post into an unpublished Practice Hub draft. Caller owns field mapping. */
export const createPracticeLibraryEntryFromBlogPost = createServerFn({ method: "POST" })
  .middleware([requireAdmin])
  .validator((d) => promoteFromBlogInput.parse(d))
  .handler(async ({ data, context }) => {
    const { supabase } = context;

    const { data: existing, error: existingError } = await supabase
      .from("practice_library_entries")
      .select("id")
      .eq("source_blog_post_id", data.sourceBlogPostId)
      .maybeSingle();
    if (existingError) throw new Error(existingError.message);
    if (existing) {
      throw new Error("This post is already in the Practice Hub.");
    }

    const excerpt = buildExcerpt(data.body_markdown, data.excerpt);
    const slug = await ensureUniqueSlug(
      supabase,
      "practice_library_entries",
      slugifyTitle(data.title),
      undefined,
      "drill",
    );
    const { data: entry, error } = await supabase
      .from("practice_library_entries")
      .insert({
        slug,
        title: data.title,
        body_markdown: data.body_markdown,
        excerpt,
        topic: data.topic,
        preview_image_url: data.preview_image_url,
        source_blog_post_id: data.sourceBlogPostId,
        published: false,
        published_at: toUtcIso(siteNow()),
      })
      .select(entryFields)
      .single();
    if (error) {
      if (error.code === "23505") {
        throw new Error("This post is already in the Practice Hub.");
      }
      throw new Error(error.message);
    }
    return entry;
  });

export type PracticeLibraryEntry = Awaited<
  ReturnType<typeof listAllPracticeLibraryEntries>
>[number];
